// UI language. This is the language of the *interface*, and it is independent of a family's
// authoring language: a user browsing in German may still download a family whose parameters are
// named in Russian — Revit does not translate parameter names on load. Conflating the two axes would
// promise a translation the product cannot deliver.
//
// The shipped set mirrors the Ember plugin's locales so the two products speak the same languages.

import en from "./locales/en.json";
import de from "./locales/de.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import ja from "./locales/ja.json";
import ru from "./locales/ru.json";
import zhHans from "./locales/zh-Hans.json";
import zhTW from "./locales/zh-TW.json";

export const SUPPORTED_LOCALES = ["en", "de", "es", "fr", "ja", "ru", "zh-Hans", "zh-TW"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = "en";

/** Native names for the language switcher — each language written in itself. */
export const LOCALE_NAMES: Record<SupportedLocale, string> = {
  en: "English",
  de: "Deutsch",
  es: "Español",
  fr: "Français",
  ja: "日本語",
  ru: "Русский",
  "zh-Hans": "简体中文",
  "zh-TW": "繁體中文",
};

const STORAGE_KEY = "apibim.fm.locale";

function isSupported(value: string | null | undefined): value is SupportedLocale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Maps a browser language tag to the closest shipped locale. Exact match wins; otherwise the base
 * language is matched, with Chinese split by script (Traditional → zh-TW, everything else → zh-Hans).
 */
function matchLocale(tag: string | null | undefined): SupportedLocale | null {
  if (!tag) return null;
  if (isSupported(tag)) return tag;

  const lower = tag.toLowerCase();
  if (lower.startsWith("zh")) {
    return /hant|tw|hk|mo/.test(lower) ? "zh-TW" : "zh-Hans";
  }
  const base = lower.split("-")[0];
  const hit = SUPPORTED_LOCALES.find((l) => l === base);
  return hit ?? null;
}

/** Resolves the startup locale: explicit stored choice first, then the browser's preferences, then English. */
export function resolveInitialLocale(): SupportedLocale {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* private mode / storage disabled — fall through to browser detection */
  }
  if (isSupported(stored)) return stored;

  for (const tag of navigator.languages ?? [navigator.language]) {
    const match = matchLocale(tag);
    if (match) return match;
  }

  return DEFAULT_LOCALE;
}

export function persistLocale(locale: SupportedLocale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* best-effort: the app still works, the choice just will not survive a reload */
  }
}

const messages: Record<SupportedLocale, unknown> = {
  en,
  de,
  es,
  fr,
  ja,
  ru,
  "zh-Hans": zhHans,
  "zh-TW": zhTW,
};

// The active UI language, wrapped in an object rather than exported as a bare `let`: reassigning an
// imported binding from another module is illegal under plain ESM, but mutating a property of an
// imported object is not, and that is exactly the shape every other store in this codebase already
// uses (see auth.svelte.ts, catalogStore.svelte.ts). Persisting a change to localStorage stays the
// caller's job via persistLocale(), same as it always was.
export const locale = $state<{ value: SupportedLocale }>({ value: resolveInitialLocale() });

/** Looks up a dot-separated key ("catalog.scopePublic") in a locale's nested message tree. */
function lookup(tree: unknown, key: string): string | undefined {
  let node: unknown = tree;
  for (const part of key.split(".")) {
    if (typeof node !== "object" || node === null) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === "string" ? node : undefined;
}

/**
 * Translates a key against the active locale, falling back to English and then to the key itself.
 * No pluralization: the shipped messages never use vue-i18n's `|` plural syntax, so a plain
 * `{name}`-style named-interpolation replace covers every message in the catalog.
 */
export function t(key: string, params?: Record<string, string | number | null | undefined>): string {
  const template = lookup(messages[locale.value], key) ?? lookup(messages[DEFAULT_LOCALE], key) ?? key;
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name] ?? "") : match,
  );
}
